import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { useIntl } from "react-intl";
import { useLocation } from "react-router-dom";
import { motion, MotionConfig } from "framer-motion";
import {
  Box,
  GlobalStyles,
} from "@mui/material";
import {
  LayoutDashboard,
  Briefcase,
  ClipboardList,
  User,
  FileText,
} from "lucide-react";
import trackEvent from "../../common/utils/trackEvent";
import useProfileCompletionBoost from "bundles/Candidate/hooks/useProfileCompletionBoost";
import OnboardingPage from "./OnboardingPage";
import DashboardTab from "./CandidateDashboard/DashboardTab";
import JobsTab from "./CandidateDashboard/JobsTab";
import ApplicationsTab from "./CandidateDashboard/ApplicationsTab";
import ProfileTab from "./CandidateDashboard/ProfileTab";
import ResumeTab from "./CandidateDashboard/ResumeTab";

const tabs = [
  { id: "dashboard", labelId: "candidate.dashboard_nav.dashboard", icon: LayoutDashboard },
  { id: "jobs", labelId: "candidate.dashboard_nav.jobs", icon: Briefcase },
  { id: "applications", labelId: "candidate.dashboard_nav.applications", icon: ClipboardList },
  { id: "profile", labelId: "candidate.dashboard_nav.profile", icon: User },
  { id: "cv", labelId: "candidate.dashboard_nav.cv", icon: FileText },
];

const VALID_TABS = new Set(["dashboard", "jobs", "applications", "profile", "cv"]);

const getInitialTab = () => {
  const tab = new URLSearchParams(window.location.search).get("tab");
  return VALID_TABS.has(tab) ? tab : "dashboard";
};

const CandidateDashboard = () => {
  const intl = useIntl();
  const [showOnboarding] = useState(
    () =>
      new URLSearchParams(window.location.search).get("showOnboarding") ===
      "true"
  );
  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [mountedTabs, setMountedTabs] = useState(() => new Set([getInitialTab()]));
  const [preSelectedJob, setPreSelectedJob] = useState(null);
  const [jobsFilterRequest, setJobsFilterRequest] = useState(null);
  const [profileSectionRequest, setProfileSectionRequest] = useState(null);

  const location = useLocation();
  const lastLocationKey = useRef(location.key);
  const activeTabRef = useRef(activeTab);
  const scrollPositions = useRef({});
  const pendingScroll = useRef(null);

  useProfileCompletionBoost();

  useEffect(() => {
    if (!showOnboarding) trackEvent("candidate_dashboard", "viewed");
  }, []);

  // Show HubSpot widget only on dashboard tab
  const applyHubspotVisibility = (tabId) => {
    if (window.HubSpotConversations?.widget) {
      if (tabId === "dashboard") {
        window.HubSpotConversations.widget.load();
      } else {
        window.HubSpotConversations.widget.remove();
      }
    }
  };

  const activateTab = (tabId) => {
    scrollPositions.current[activeTabRef.current] = window.scrollY;
    pendingScroll.current = scrollPositions.current[tabId] ?? 0;
    activeTabRef.current = tabId;
    setActiveTab(tabId);
    setMountedTabs((prev) => {
      if (prev.has(tabId)) return prev;
      const next = new Set(prev);
      next.add(tabId);
      return next;
    });
    applyHubspotVisibility(tabId);
  };

  const handleTabChange = (tabId, job = null, opts = {}) => {
    trackEvent("candidate_dashboard", `tab_${tabId}`);
    if (job) setPreSelectedJob(job);
    if (opts.onlyFavorites) setJobsFilterRequest({ onlyFavorites: true });
    if (opts.profileSection) setProfileSectionRequest(opts.profileSection);
    activateTab(tabId);

    // Push (not replace) so the phone back button / swipe-back walks back
    // through tabs instead of leaving the dashboard.
    const url = new URL(window.location.href);
    url.searchParams.set("tab", tabId);
    if (tabId !== "jobs") url.searchParams.delete("jid");
    if (tabId !== "applications") url.searchParams.delete("view");
    window.history.pushState(null, "", url.toString());
  };

  useEffect(() => {
    const onPopState = () => {
      const tab = new URLSearchParams(window.location.search).get("tab");
      activateTab(VALID_TABS.has(tab) ? tab : "dashboard");
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useLayoutEffect(() => {
    if (pendingScroll.current === null) return;
    const top = pendingScroll.current;
    pendingScroll.current = null;
    window.scrollTo(0, top);
  }, [activeTab]);

  useEffect(() => {
    if (location.key === lastLocationKey.current) return;
    lastLocationKey.current = location.key;
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    if (params.get("onlyFavorites") === "true") setJobsFilterRequest({ onlyFavorites: true });
    activateTab(VALID_TABS.has(tab) ? tab : "dashboard");
  }, [location.key, location.search]);

  const tabContent = (id) => {
    switch (id) {
    case "dashboard":
      return <DashboardTab onNavigate={handleTabChange} active={activeTab === "dashboard"} />;
    case "jobs":
      return (
        <JobsTab
          preSelectedJob={preSelectedJob}
          onPreSelectedJobConsumed={() => setPreSelectedJob(null)}
          filterRequest={jobsFilterRequest}
          onFilterRequestConsumed={() => setJobsFilterRequest(null)}
          onNavigate={handleTabChange}
        />
      );
    case "applications":
      return <ApplicationsTab onNavigate={handleTabChange} active={activeTab === "applications"} />;
    case "profile":
      return (
        <ProfileTab
          sectionRequest={profileSectionRequest}
          onSectionRequestConsumed={() => setProfileSectionRequest(null)}
        />
      );
    case "cv":
      return <ResumeTab onNavigate={handleTabChange} />;
    default:
      return null;
    }
  };

  if (showOnboarding) return <OnboardingPage />;

  return (
    <MotionConfig reducedMotion="user">
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "hsla(160, 10%, 96%, 0.3)",
          pb: { xs: "64px", sm: 0 },
          // `clip` (not `hidden`) keeps the sticky nav working: it absorbs the
          // scrollbar gutter of the kanban board's 100vw full-bleed without
          // turning this into a scroll container.
          overflowX: "clip",
        }}
      >
        {/* Keep the HubSpot bubble clear of the fixed bottom nav on phones */}
        <GlobalStyles styles={{
          "@media (max-width: 599px)": {
            "#hubspot-messages-iframe-container": {
              bottom: "76px !important",
            },
          },
          "button:focus-visible, a:focus-visible": {
            outline: "2px solid hsl(153, 68%, 25%)",
            outlineOffset: "2px",
            borderRadius: "4px",
          },
        }} />
        {/* Desktop tab navigation */}
        <Box
          component="nav"
          sx={{
            display: { xs: "none", sm: "block" },
            bgcolor: "#F9F9F9",
            backdropFilter: "blur(12px)",
            borderBottom: "1px solid hsl(160, 10%, 90%)",
            position: "sticky",
            top: 0,
            zIndex: 40,
            mt: "40px",
          }}
        >
          <Box
            sx={{
              maxWidth: "960px",
              mx: "auto",
              px: { xs: "16px", sm: "24px", lg: "32px" },
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "stretch" }}>
              {tabs.map((tab) => {
                const badge = null;
                const isActive = activeTab === tab.id;
                const Icon = tab.icon;
                return (
                  <Box
                    key={tab.id}
                    component="button"
                    onClick={() => handleTabChange(tab.id)}
                    sx={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      px: "24px",
                      pt: "20px",
                      pb: "16px",
                      fontSize: "15px",
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                      transition: "color 0.15s",
                      color: isActive ? "hsl(178, 93%, 10%)" : "hsla(178, 93%, 10%, 0.7)",
                      cursor: "pointer",
                      border: "none",
                      bgcolor: "transparent",
                      "&:hover": {
                        color: "hsl(178, 93%, 10%)",
                      },
                    }}
                  >
                    <Icon
                      size={22}
                      strokeWidth={1.8}
                      color={isActive ? "hsl(178, 93%, 10%)" : "hsla(178, 93%, 10%, 0.5)"}
                    />
                    <span style={{ fontFamily: isActive ? "Segoe UI Bold, sans-serif, system-ui" : "Segoe UI Semibold, sans-serif, system-ui", color: isActive ? "hsl(178, 93%, 10%)" : "inherit" }}>
                      {intl.formatMessage({ id: tab.labelId })}
                    </span>
                    {badge && (
                      <Box
                        component="span"
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          minWidth: "22px",
                          height: "22px",
                          borderRadius: "9999px",
                          bgcolor: "hsl(178, 93%, 10%)",
                          color: "hsl(0, 0%, 100%)",
                          fontSize: "11px",
                          fontWeight: 700,
                          px: "6px",
                        }}
                      >
                        {badge}
                      </Box>
                    )}
                    {isActive && (
                      <motion.div
                        layoutId="activeTabDesktop"
                        style={{
                          position: "absolute",
                          bottom: 0,
                          left: "8px",
                          right: "8px",
                          height: "3px",
                          background: "hsl(178, 93%, 10%)",
                          borderRadius: "3px 3px 0 0",
                        }}
                      />
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>

        {/* Content */}
        <Box
          component="main"
          sx={{
            maxWidth: "1280px",
            mx: "auto",
            px: { xs: "16px", sm: "24px", lg: "32px" },
            py: "24px",
          }}
        >
          {tabs.map((tab) => {
            if (!mountedTabs.has(tab.id)) return null;
            const isVisible = activeTab === tab.id;
            return (
              <motion.div
                key={tab.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 8 }}
                transition={{ duration: 0.25 }}
                style={{ display: isVisible ? "block" : "none" }}
              >
                {tabContent(tab.id)}
              </motion.div>
            );
          })}
        </Box>

        {/* Mobile bottom navigation */}
        <Box
          component="nav"
          sx={{
            display: { xs: "block", sm: "none" },
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            bgcolor: "hsla(0, 0%, 100%, 0.95)",
            backdropFilter: "blur(12px)",
            zIndex: 50,
            pb: "env(safe-area-inset-bottom, 0px)",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-around", alignItems: "center", height: "68px", px: "4px" }}>
            {tabs.map((tab) => {
              const badge = null;
              const isActive = activeTab === tab.id;
              const Icon = tab.icon;
              return (
                <Box
                  key={tab.id}
                  component="button"
                  onClick={() => handleTabChange(tab.id)}
                  sx={{
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "4px",
                    py: "6px",
                    px: "6px",
                    minWidth: "48px",
                    transition: "all 0.15s",
                    cursor: "pointer",
                    border: "none",
                    bgcolor: "transparent",
                  }}
                >
                  <Box sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "40px",
                    height: "28px",
                    borderRadius: "9999px",
                    transition: "all 0.2s",
                    bgcolor: isActive ? "hsla(178, 93%, 10%, 0.1)" : "transparent",
                  }}>
                    <Icon
                      size={18}
                      strokeWidth={isActive ? 2.2 : 1.6}
                      style={{ color: isActive ? "hsl(153, 68%, 25%)" : "hsl(178, 10%, 45%)", transition: "color 0.15s" }}
                    />
                    {badge && (
                      <Box
                        component="span"
                        sx={{
                          position: "absolute",
                          top: "-4px",
                          right: "-4px",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          minWidth: "15px",
                          height: "15px",
                          borderRadius: "9999px",
                          bgcolor: "hsl(153, 68%, 25%)",
                          color: "hsl(0, 0%, 100%)",
                          fontSize: "8px",
                          fontFamily: "Segoe UI Bold, sans-serif, system-ui",
                          px: "2px",
                        }}
                      >
                        {badge}
                      </Box>
                    )}
                    {tab.id === "cv" && !badge && (
                      <Box
                        component="span"
                        sx={{
                          position: "absolute",
                          top: "-2px",
                          right: "-6px",
                          display: "inline-flex",
                          alignItems: "center",
                          px: "4px",
                          py: "1px",
                          borderRadius: "9999px",
                          bgcolor: "hsl(178, 93%, 10%)",
                          color: "hsl(0, 0%, 100%)",
                          fontSize: "9px",
                          fontFamily: "Segoe UI Bold, sans-serif, system-ui",
                        }}
                      >
                        {intl.formatMessage({ id: "candidate.dashboard_nav.new_badge" })}
                      </Box>
                    )}
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      fontSize: "10px",
                      whiteSpace: "nowrap",
                      transition: "color 0.15s",
                      fontFamily: isActive ? "Segoe UI Bold, sans-serif, system-ui" : "Segoe UI Regular, sans-serif, system-ui",
                      color: isActive ? "hsl(178, 93%, 10%) !important" : "hsl(178, 10%, 45%)",
                    }}
                  >
                    {intl.formatMessage({ id: tab.labelId })}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </MotionConfig>
  );
};

export default CandidateDashboard;
